/**
 * EC2 instance-type → max_agents capacity lookup.
 *
 * Single source of truth for "how many agents fit on this host". Read by:
 *  - API provision + resize-commit paths to persist `hosts.max_agents`
 *  - API enforcement paths (`/hosts/:name/assign`, `/agents/create-full`,
 *    `/agents/:codeName/migrate`) via the persisted column
 *  - Webapp host-create / picker UI so the displayed cap matches enforcement
 *
 * Numbers are deliberately conservative — sized for "the manager isn't
 * struggling" rather than "absolute upper bound". Operators who want more
 * can resize to a larger type; auto-resize is intentionally out of scope.
 *
 * Pure functions, no node-only dependencies — safe for browser/edge bundles.
 */

// Sizing model (recalibrated from live fleet telemetry — the early
// memory-leak issues that forced the original ~2GB/agent budget are resolved).
// Observed footprint is ~0.6GB/agent + ~1GB base (agt-aws-1: 13 agents/16GB at
// 62%; two t3.large hosts at 4-5 agents/40-43%). That lands almost exactly on a
// simple, memorable rule: **one agent per GB of RAM** (floor of 1). The
// host-capacity-monitor alert (one slot left) is the safety net that prompts a
// resize before a host actually saturates, so the table can size to real
// capacity rather than the old ultra-conservative numbers.
const CAPACITY_TABLE: Record<string, number> = {
  // t3 family — burstable (CPU credits). Fine for these mostly-I/O-bound
  // agents; the monitor + resize path cover the rare sustained-load case.
  't3.micro': 1, //    2 vCPU /  1 GB — floor
  't3.small': 2, //    2 vCPU /  2 GB
  't3.medium': 4, //   2 vCPU /  4 GB
  't3.large': 8, //    2 vCPU /  8 GB
  't3.xlarge': 16, //  4 vCPU / 16 GB
  't3.2xlarge': 32, // 8 vCPU / 32 GB
  // m6i family — fixed performance (no burst-credit cliff), the preferred
  // family for dense / sustained multi-agent hosts (e.g. Enterprise default).
  'm6i.large': 8, //    2 vCPU /  8 GB
  'm6i.xlarge': 16, //  4 vCPU / 16 GB
  'm6i.2xlarge': 32, // 8 vCPU / 32 GB
};

/** Conservative cap for unknown / null instance types. */
const FALLBACK = 1;

/**
 * Resolve the agent-capacity cap for an EC2 instance type.
 *
 * Null / undefined / unknown types → FALLBACK (1). Self-managed hosts
 * with no `ec2_instance_type` set, and any newly-released instance
 * family we haven't catalogued yet, both fall through here. A console
 * warning fires on the unknown-but-non-null branch so prod logs
 * surface the lookup gap before an operator hits the cap and is
 * confused.
 */
export function maxAgentsForInstanceType(
  instanceType: string | null | undefined,
): number {
  if (instanceType == null) return FALLBACK;
  const cap = CAPACITY_TABLE[instanceType];
  if (cap === undefined) {
    console.warn(
      `[ec2-capacity] unknown instance type "${instanceType}" — falling back to ${FALLBACK} agent. ` +
        `Add a row to CAPACITY_TABLE in packages/core/src/provisioning/ec2-capacity.ts.`,
    );
    return FALLBACK;
  }
  return cap;
}

/** Exposed for tests + UI labels — the catalogued types in deterministic order. */
export const KNOWN_INSTANCE_TYPES: ReadonlyArray<string> = Object.keys(CAPACITY_TABLE);

export const FALLBACK_CAPACITY = FALLBACK;
